import React, {useCallback, useEffect, useMemo, useState} from "react";
import ValidateMessages from "../../../../../../../../core/models/static/texts/vallidate-messages";
import {openLinkInNewTab} from "../../../../../../../../core/services/utils/utils";
import {routes} from "../../../../../../../../core/models/static/routes/app-routes";
import Form from "../../../../../../../components/base/form";
import {Col, Row} from "reactstrap";
import {OutlinedInputForm} from "../../../../../../../components/base/input/outlined_input";
import {OutlinedDropdown, OutlinedDropdownForm} from "../../../../../../../components/base/dropdown/outlined_dropdown";
import Button from "../../../../../../../components/base/button/button";
import {CheckboxForm} from "../../../../../../../components/base/checkbox/checkbox";
import Chips, {ChipsContentType, ChipsIconPosition, ChipsTypes} from "../../../../../../../components/base/chips/chips";
import {Fade, SvgIcon, Typography} from "@mui/material";
import IconButton from "../../../../../../../components/base/button/icon_button";
import {ReactComponent as LinkIcon} from "../../../../../../../../assets/images/notifications/link.svg";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../../core/services/api/api";
import TestNotificationDialog from "../../../../../../../components/dialogs/test-notifications";
import useIndustry from "../../../../../../../hooks/use-industry";
import {selectedCategoryHook} from "../../../../../../../hooks/use-selected-category";
import ProductInformationPopover from "../../../../../../../components/app-specific/draggable-popover/product-information-popover";
import CustomerMemberAutocomplete from "../../../../../../../components/text-fields/autocomplete/customer-member-autocomplete";
import {MuiRFF, Yup} from "../../../../../../../components/form";
import {useHistory, useLocation} from "react-router-dom";
import PartAutocomplete from "../../../../../../../components/text-fields/autocomplete/part-autocomplete";


const NotificationDataTypes = {
    partNo: {name: 'Part Number', id: 1},
    category: {name: 'Category', id: 2},
    url: {name: 'Url', id: 3},
    keyword: {name: "Keyword", id: 4},
}

/**
 * Fill the initial values of the form based on the provided notification Data.
 *
 * @param notificationData
 * @param industries
 */
const fillInitialValuesBasedOnNotificationData = (notificationData, industries) => {
    if (!notificationData || !Object.keys(notificationData)?.length) {
        return {}
    }
    if (notificationData?.PartNo?.length) {
        return {
            dataType: NotificationDataTypes.partNo,
            part: {
                partNo: notificationData?.PartNo,
            },
        }
    }
    if (notificationData?.Category) {
        return {
            dataType: NotificationDataTypes.category,
            category: notificationData?.Category,
            industry: industries.find(e => notificationData.industryId === e.id),
        }
    }
    if (notificationData?.Url) {
        return {
            dataType: NotificationDataTypes.url,
            url: notificationData?.PartNo,
        }
    }
    if (notificationData?.Keyword) {
        return {
            dataType: NotificationDataTypes.keyword,
            keyword: notificationData?.Keyword,
        }
    }
}


/**
 * Creates the schema layout and initial values of the form based on the provided data-type
 * @param dataType
 * @return {{initialValues, schema: {dataType, description, title, type, users}}}
 */
const createSchemaBasedOnDataType = (dataType) => {
    const schema = {
        users: Yup.array().nullable().default([]),
        title: Yup.string().nullable().required(ValidateMessages.required),
        type: Yup.object().nullable().required(ValidateMessages.required),
        description: Yup.string().nullable().required(ValidateMessages.required),
        dataType: Yup.object().nullable().required(ValidateMessages.required),
        sendToAll: Yup.boolean().nullable().default(false),
        test: Yup.boolean().nullable().default(false)
    }
    switch (dataType?.id) {
        case NotificationDataTypes.partNo.id:
            schema['part'] = Yup.mixed().nullable().required(ValidateMessages.required)
            break;
        case NotificationDataTypes.category.id:
            schema['industry'] = Yup.object().nullable()
            schema['category'] = Yup.object().nullable().required(ValidateMessages.required)
            break;
        case NotificationDataTypes.url.id:
            schema['url'] = Yup.string().nullable().required(ValidateMessages.required)
            break;
        case NotificationDataTypes.keyword.id:
            schema['keyword'] = Yup.string().nullable().required(ValidateMessages.required)
            break;
        default:
            break;
    }
    return Yup.object().shape(schema)
}

/**
 * Create the schema and initial values of the form based on the provided notification history.
 * @param notificationHistory the notification object to fill the form based off of,
 * @param types
 * @param industries
 * @return {{}}
 */
const createSchemaAndInitialValuesBasedOnHistory = (notificationHistory, types, industries) => {
    const data = {
        users: [],
        title: notificationHistory?.title,
        type: types?.find(e => e.id === notificationHistory?.typeID) ?? null,
        description: notificationHistory?.body,
        ...(notificationHistory?.data ? {
            ...fillInitialValuesBasedOnNotificationData(JSON.parse(notificationHistory?.data), industries)
        } : {}),
    }
    return {
        initialValues: data,
        schema: createSchemaBasedOnDataType(data.dataType),
    }
}

const NotificationsFormView = ({notificationHistory}) => {
    const [schema, setSchema] = useState(Yup.object().shape({}))
    const [initialValues, setInitialValues] = useState({})
    const [sending, setSending] = useState(false)
    const [testNotification, setTestNotification] = useState({open: false, values: {}})
    const {industries, fetchIndustry, categoriesOfFetchedIndustry, hasNoIndustries} = useIndustry();
    const isMounted = useIsMounted();

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const history = useHistory();
    const location = useLocation();
    const [notificationTypes, setNotificationTypes] = useState([])
    const [loading, setLoading] = useState(true)

    /**
     * Handles the api errors when dealing with Biz-Live system.
     *
     * in both cases, removes the access of this part of the system.
     * @type {(function(*): void)}
     */
    const unAuthorizedOrAccessDeniedCallback = useCallback((response) => {
        if (!isMounted()) return;
        switch (response?.errorCode) {
            case 401:
            case 403:
            case -401:
            case -403:
                history.replace(`${location.pathname}${routes.error.accessDeniedForSection}`);
                break;
            default:
                break;
        }
    }, [history, isMounted, location.pathname])

    /**
     * the notification types and categories from the server for the form. populates the internal state list with
     * the result of the server.
     * @type {(function(): Promise<void>)}
     */
    const getFormData = useCallback(async () => {
        setLoading(true)
        const response = await Api.getNotificationTypes(unAuthorizedOrAccessDeniedCallback)
        if (!isMounted())
            return;
        setLoading(false)
        if (response?.resultFlag) {
            setNotificationTypes(response?.data ?? [])
        }
    }, [isMounted, unAuthorizedOrAccessDeniedCallback])

    /**
     * As soon as the component mounts:
     * fetches the notification types and categories from the server for the form.
     */
    useEffect(() => {
        void getFormData()
    }, [getFormData])

    /**
     * As soon as the notification history changes, fetches the industry related to the notification history's data.
     */
    useEffect(() => {
        if (notificationHistory) {
            void fetchIndustry({industryId: notificationHistory.industryId});
        }
    }, [notificationHistory, fetchIndustry]);

    /**
     * Listens for the changes in notification history and with each change:
     *  if the data is not being loaded, creates the new schema and initial values for the given notificationHistory.
     */
    useEffect(() => {
        if (loading)
            return
        const {schema, initialValues} = createSchemaAndInitialValuesBasedOnHistory(
            notificationHistory,
            notificationTypes,
            industries,
        )
        setSchema(schema)
        setInitialValues(initialValues)
        // TODO: notification history --> needs to have industry attached to it.
    }, [notificationHistory, loading, notificationTypes, industries])

    /**
     * Sends a new wave of notifications to the specified users in the system.
     * @type {(function(*): Promise<void>)}
     */
    const sendNotification = useCallback(async (values) => {
        const forApi = {
            title: values.title,
            description: values.description,
            typeID: values.type.id,
            isTest: values.test,
            sendToAll: values.sendToAll,
            targetUserId: values.users.map(e => e.id.toString()) ?? [],
            data: {
                partNo: values.part?.partNo,
                url: values.url,
                keyword: values.keyword,
                category: values.category,
            },
        }
        if (categoriesOfFetchedIndustry.some(e => e.parentId === forApi.data.category?.id)) {
            return {'category': ValidateMessages.required};
        }
        if (forApi.isTest) {
            setTestNotification({open: true, values: forApi})
            return;
        }
        if (!forApi.sendToAll && !forApi.targetUserId?.length) {
            return {'users': 'At least one user must be selected.'}
        }
        setSending(true)
        await Api.sendNotification(forApi);
        if (!isMounted())
            return
        setSending(false)
    }, [isMounted, categoriesOfFetchedIndustry])

    /**
     * Removes the previous data related to the previous dataType
     * @param {formApi} form
     * @param {any} values
     * @param {{id: number}} dataType
     */
    const onDataTypeChanged = useCallback((form, values, dataType) => {
        switch (values?.dataType?.id) {
            case NotificationDataTypes.partNo.id:
                form.change('part', null)
                break;
            case NotificationDataTypes.category.id:
                form.change('category', null)
                break;
            case NotificationDataTypes.url.id:
                form.change('url', null)
                break;
            case NotificationDataTypes.keyword.id:
                form.change('keyword', null)
                break;
            default:
                break;
        }
        setSchema(createSchemaBasedOnDataType(dataType))
    }, [])

    return (
        <>
            <Form
                onSubmit={sendNotification}
                validate={validate}
                initialValues={initialValues}
                render={({values, form}) => {
                    return (
                        <Row className='no-gutters justify-content-center'>
                            <Col xs={12} className='px-2 mb-2'>
                                <Typography variant={'h6'} fontWeight={600} marginBlock={2}>
                                    Send Custom Notification
                                </Typography>
                            </Col>
                            <Col xs={12} className='px-2 mb-3'>
                                <OutlinedInputForm
                                    label={"Title"}
                                    placeholder={'title of notification'}

                                    name={'title'}
                                    required={required.title}
                                    value={values.title ?? ''}
                                    onChange={(e) => form.change(e.target.name, e.target.value)}
                                />
                            </Col>
                            <Col xs={12} className='px-2 mb-3'>
                                <OutlinedDropdownForm
                                    label={'Type'}

                                    placeholder={'type of the notification'}
                                    name={`type`}
                                    required={required.type}
                                    data={notificationTypes?.map(e => ({
                                        value: e,
                                        label: e.title,
                                        id: e.id
                                    })) ?? []}
                                />
                            </Col>
                            <Col xs={12} className='px-2 mb-3'>
                                <OutlinedInputForm
                                    multiline
                                    minRows={8}
                                    label={"Description"}

                                    placeholder={'description of notification'}
                                    name={'description'}
                                    required={required.description}
                                    value={values.description ?? ''}
                                    onChange={(e) => form.change(e.target.name, e.target.value)}
                                />
                            </Col>


                            <Col xs={12} className='px-2 mb-2 mt-3'>
                                <Typography variant={'subtitle1'} fontWeight={600}>
                                    Data
                                </Typography>
                            </Col>
                            <Col xs={12} className='px-2 mb-3'>
                                <OutlinedDropdownForm
                                    label={'Category'}
                                    placeholder={'category of the notification data'}
                                    name={`dataType`}
                                    required={required.dataType}
                                    onChange={(e) => onDataTypeChanged(form, values, e.target.value)}
                                    data={Object.values(NotificationDataTypes)?.map(e => ({
                                        value: e,
                                        label: e.name,
                                        id: e.id
                                    })) ?? []}
                                />
                            </Col>
                            {
                                values?.dataType?.id === NotificationDataTypes.partNo.id &&
                                <PartNoSection
                                    form={form}
                                    required={required}
                                    values={values}
                                />
                            }

                            {
                                values?.dataType?.id === NotificationDataTypes.category.id &&
                                <CategoriesSection
                                    form={form}
                                    values={values}
                                    required={required}
                                    fetchIndustry={fetchIndustry}
                                    industries={industries}
                                    categories={categoriesOfFetchedIndustry}
                                    hasNoIndustries={hasNoIndustries}
                                />
                            }

                            {
                                values?.dataType?.id === NotificationDataTypes.url.id &&
                                <UrlSection
                                    form={form}
                                    values={values}
                                    required={required}
                                />
                            }

                            {
                                values?.dataType?.id === NotificationDataTypes.keyword.id &&
                                <KeywordSection
                                    form={form}
                                    values={values}
                                    required={required}
                                />
                            }

                            <Col xs={12} className='px-2 mb-2 mt-3'>
                                <div className={'d-flex w-100 justify-content-end'}>
                                    <Button
                                        className='font-weight-semi-bold letter-space-sm py-2 px-5'
                                        variant={'outlined'}
                                        color={'primary'}
                                        disabled={sending || loading}
                                        onClick={() => {
                                            form.change('test', true);
                                            form.submit()
                                        }}
                                    >
                                        {
                                            sending
                                                ? "Sending..."
                                                : "Test"
                                        }
                                    </Button>
                                </div>
                            </Col>

                            <Col xs={12} className='px-2 mb-2 mt-4 d-flex align-items-center justify-content-between'>
                                <Typography variant={'subtitle1'} fontWeight={600}>
                                    Users
                                </Typography>
                                <div className={'send-to-all'}>
                                    <CheckboxForm
                                        name={'sendToAll'}
                                        required={required.sendToAll}
                                        onChange={(e) => {
                                            form.change('sendToAll', e.target.checked);
                                            form.change('users', [])
                                        }}
                                        data={{
                                            value: values.sendToAll ?? false,
                                            label: 'Send to All'
                                        }}
                                    />
                                </div>
                            </Col>
                            <Col xs={12} className='px-2 mb-2'>
                                <CustomerMemberAutocomplete
                                    multiple
                                    form
                                    name={'users'}
                                    required={required.users}
                                    label={'Target Users'}
                                />
                            </Col>
                            <Col xs={12} className='px-2 mb-2 mt-3'>
                                <div className={'d-flex w-100 justify-content-end'}>
                                    <Button
                                        className='px-5'
                                        variant={'contained'}
                                        color={'primary'}
                                        onClick={() => {
                                            form.change('test', false)
                                            form.submit()
                                        }}
                                        disabled={sending || loading}
                                    >
                                        {
                                            sending
                                                ? "Sending..."
                                                : "Send"
                                        }
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    )
                }}
            />
            <TestNotificationDialog
                open={testNotification.open}
                values={testNotification.values}
                setOpen={() => setTestNotification({open: false, values: null})}
            />
        </>
    )
}

const PartNoSection = ({form, values, required}) => {


    return (
        <>
            <Fade
                in={!!values.part?.partNo?.length}
                unmountOnExit
                mountOnEnter
                className={'d-flex justify-content-start w-100 ml-2'}>
                <div className={'d-flex flex-column'}>
                    <Chips
                        iconPosition={ChipsIconPosition.start}
                        type={ChipsTypes.remove}
                        contentType={ChipsContentType.element}
                        content={
                            <ProductInformationPopover partNo={values.part?.partNo}>
                                {values.part?.partNo ?? ''}
                            </ProductInformationPopover>
                        }
                        onIconClick={() => form.change('partNo', null)}
                    />
                </div>
            </Fade>
            <Fade
                in={!values.part?.partNo?.length}
                unmountOnExit
                mountOnEnter
                className={'w-100 px-2 mb-3'}>
                <div className={'col-12'}>
                    <PartAutocomplete
                        form
                        disabled={!!values.part?.partNo?.length}
                        label={"Products"}
                        name={'part'}
                        required={required.partNo}
                    />
                </div>
            </Fade>
        </>
    );
}

const CategoriesSection = ({form, values, categories, fetchIndustry, industries, required, hasNoIndustries}) => {
    const {getSubCategories, getSelectedCategoriesHierarchy} = selectedCategoryHook(categories, values.category?.id);
    const selectedCategoriesHierarchy = getSelectedCategoriesHierarchy();
    const subCategories = getSubCategories();

    const onIndustryChanged = useCallback((industry) => {
        void fetchIndustry({industryId: industry.id});
        form.change('industry', industry);
        form.change('category', null);
    }, [fetchIndustry, form])

    return (
        <>
            {
                industries.length > 1 &&
                <Col xs={12} className={'px-2 mb-3'}>
                    <OutlinedDropdown
                        label={'Industry'}
                        onChange={(e) => onIndustryChanged(e)}
                        value={values.industry}
                        data={industries?.map(e => ({value: e, label: e.title, id: e.id})) ?? []}
                    />
                </Col>
            }
            {
                !hasNoIndustries &&
                <>
                    {
                        selectedCategoriesHierarchy.map((category, index) => (
                            <Col xs={12} key={`category-${category.id}`} className={'px-2 mb-3'}>
                                <OutlinedDropdown
                                    label={index === 0 ? `Category` : "Sub category"}
                                    data={category.options?.map(e => ({label: e.title, value: e, id: e.id})) ?? []}
                                    onChange={(category) => form.change('category', category)}
                                    value={category}
                                    fullWidth
                                    SelectProps={{
                                        renderValue: () => category.title,
                                    }}
                                />
                            </Col>
                        ))
                    }
                    {
                        subCategories.length > 0 &&
                        <Col xs={12} className={'px-2 mb-3'}>
                            <OutlinedDropdownForm
                                name={'category'}
                                label={selectedCategoriesHierarchy.length ? "Sub category" : "Category"}
                                data={subCategories?.map(e => ({label: e.title, value: e, id: e.id,})) ?? []}
                                onChange={(e) => form.change('category', e.target.value)}
                                value={""}
                                required={required.category}
                                fullWidth
                                SelectProps={{
                                    renderValue: (e) => e?.title,
                                }}
                            />
                        </Col>
                    }
                </>
            }
        </>
    );
}

const UrlSection = ({form, values, required}) => {
    const canShow = !!values?.url?.match(/^(http|https):\/\//)

    /**
     * Opens the given url in a new tab of the browser
     * @param {string} url the url of the new tab
     */
    const openUrlInNewTab = (url) => {
        openLinkInNewTab(url)
    }

    return (
        <>
            <Col xs={12} className={'px-2 mb-3 d-flex align-items-center position-relative'}>
                <OutlinedInputForm
                    label={"Url"}

                    placeholder={'url of notification data'}
                    name={'url'}
                    required={required.url}
                    value={values.url ?? ''}
                    onChange={(e) => form.change(e.target.name, e.target.value)}
                />
                <Fade in={canShow} style={{right: -35}} className={'position-absolute link-button'}>
                    <IconButton
                        disabled={!canShow}
                        color={'secondary'}
                        onPressed={() => openUrlInNewTab(values?.url)}
                    >
                        <SvgIcon component={LinkIcon}/>
                    </IconButton>
                </Fade>

            </Col>
        </>
    );
}

const KeywordSection = ({form, values, required}) => {

    return (
        <>
            <Col xs={12} className={'px-2 mb-3 d-flex align-items-center position-relative'}>
                <OutlinedInputForm
                    label={"Keywords"}
                    placeholder={'keyword of notification data'}
                    name={'keyword'}
                    required={required.keyword}
                    value={values.keyword ?? ''}
                    onChange={(e) => form.change(e.target.name, e.target.value)}
                />

            </Col>
        </>
    );
}


export default NotificationsFormView;
